import { type FC, memo } from "react";

import { type ProductData } from "~/domains/product/types/ProductData";
import Spacer from "~/common/components/Spacer";
import { Carousel } from "~/common/components/carousel/Carousel";
import classNames from "classnames";

import { MobileScrollableTileContainer } from "~/features/productDetails/components/RelatedProducts/MobileScrollableTileContainer";
import {
  DesktopRelatedProductCard,
  MobileRelatedProductCard,
} from "~/features/productDetails/components/RelatedProducts/RelatedProductCard";
import { Col, Row } from "~/common/components/Layout";
import useMediaQuery from "~/common/hooks/useMediaQuery";
import { MEDIA_QUERY_MIN_DESKTOP_S } from "~/common/utils/screenSizes";
import { useTranslation } from "react-i18next";

export type RelatedProductData = Pick<
  ProductData,
  | "designation"
  | "sapMaterialNumber"
  | "aliases"
  | "shortDescription"
  | "preferred"
  | "url"
  | "images"
  | "manufacturerLogoUrl"
  | "stockAmount"
>;

interface RelatedProductsBlockProps {
  mainProductDesignation: string;
  relatedProducts: RelatedProductData[];
  className?: string;
}

const RelatedProductsBlock: FC<RelatedProductsBlockProps> = ({
  className,
  relatedProducts,
  mainProductDesignation,
}) => {
  const { t } = useTranslation();

  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);

  if (relatedProducts.length === 0) {
    return null;
  }

  const content = isDesktop ? (
    <Carousel
      data={relatedProducts}
      pageItemsCount={3}
      renderSlide={(data) => (
        <Row>
          {data.data.map((item) => (
            <Col size={4}>
              <DesktopRelatedProductCard
                key={item.sapMaterialNumber}
                product={item}
                mainProductDesignation={mainProductDesignation}
              />
            </Col>
          ))}
        </Row>
      )}
    />
  ) : (
    <MobileScrollableTileContainer
      data={relatedProducts}
      render={(item) => <MobileRelatedProductCard product={item} mainProductDesignation={mainProductDesignation} />}
      getKey={(item) => item.sapMaterialNumber}
    />
  );

  return (
    <div className={classNames("scha__related-products", className)}>
      {isDesktop && <Spacer className={"mb-30"} />}
      <h3 className={"scha__related-products__title"}>{t("pdp.relatedProducts.title")}</h3>
      {content}
    </div>
  );
};

export default memo(RelatedProductsBlock);
